/**
 * useDOA — Hook for DOA (Delegation of Authority) enforcement.
 *
 * Phase 3 (multi-entity DOA):
 *   - canApprove(amount, entity_code?, category?): boolean
 *     Checks the user's effective limit for a specific (entity, category)
 *     tuple. Falls back to the org-wide generic cascade when both are
 *     omitted — that's the pre-Phase-3 behavior.
 *   - Per-tuple cache. The first call for `(USA, CONTRACT)` fetches the
 *     cascade dict, subsequent calls reuse it. Switching tuples triggers
 *     a one-time fetch per new tuple.
 *
 * Public API (additive — old `canApprove(amount)` keeps working):
 *   - canApprove(amount, entity_code?, category?) -> boolean
 *   - userLevel — current user's DOA level (L2..L7)
 *   - userLimit — generic org-wide limit (back-compat for the legacy KPI)
 *   - doaLevels — generic org-wide cascade (back-compat)
 */

import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, AGENT_ESCALATION } from '../constants';

// Cache keyed by `${entity_code || ""}::${category || ""}`. The empty key
// ("::") is the generic org-wide cascade (today's behavior). All other
// keys are per-tuple cascades fetched on demand. Partial<...> so index
// access yields `Foo | undefined` for proper TS narrowing on truthy checks.
type LevelDict = Record<string, number | null>;
const _cache: Partial<Record<string, LevelDict>> = {};
const _inflight: Partial<Record<string, Promise<LevelDict | null>>> = {};

function _cacheKey(entity_code?: string | null, category?: string | null): string {
  return `${(entity_code || '').toUpperCase()}::${(category || '').toUpperCase()}`;
}

function _fetchLevels(
  entity_code?: string | null,
  category?: string | null,
): Promise<LevelDict | null> {
  const key = _cacheKey(entity_code, category);
  const cached = _cache[key];
  if (cached) return Promise.resolve(cached);
  const inflight = _inflight[key];
  if (inflight) return inflight;

  const params = new URLSearchParams();
  if (entity_code) params.set('entity_code', entity_code);
  if (category) params.set('category', category);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const p: Promise<LevelDict | null> = authFetch(`${API_V1}/org/configuration/doa-levels${qs}`)
    .then(r => r.ok ? r.json() : null)
    .then((data: LevelDict | null) => {
      if (data) _cache[key] = data;
      return data;
    })
    .catch(() => null)
    .finally(() => { delete _inflight[key]; });

  _inflight[key] = p;
  return p;
}

export function useDOA() {
  // Re-render counter — bumped when a new tuple's cascade lands so any
  // disabled-state tied to canApprove() picks up the freshly-fetched values.
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick(t => t + 1), []);

  // Generic org-wide cascade (back-compat for the legacy `userLimit` /
  // `doaLevels` callers).
  const [generic, setGeneric] = useState<LevelDict>(_cache['::'] || {});

  // Get current user's level from localStorage
  const userStr = localStorage.getItem('agentcfo_user');
  let userLevel = 'L2';
  try {
    const user = userStr ? JSON.parse(userStr) : null;
    userLevel = user?.doa_level || user?.role || 'L2';
    if (!userLevel.startsWith('L')) {
      const esc = AGENT_ESCALATION[userLevel];
      userLevel = esc?.level || 'L2';
    }
  } catch { /* ignore */ }

  useEffect(() => {
    if (_cache['::']) {
      setGeneric(_cache['::']);
      return;
    }
    _fetchLevels().then(() => {
      if (_cache['::']) setGeneric(_cache['::']);
    });
  }, []);

  // Generic limit — for back-compat with old `userLimit`. The Phase 3
  // canApprove below uses the per-tuple cache, not this value.
  const genericLimit = (generic[userLevel] ?? AGENT_ESCALATION[userLevel]?.limit ?? 50000) as number;

  const canApprove = useCallback(
    (amount: number, entity_code?: string | null, category?: string | null): boolean => {
      if (userLevel === 'L7') return true; // Board — unlimited regardless of bucket

      const key = _cacheKey(entity_code, category);
      const cascade = _cache[key];
      if (!cascade) {
        // Cache miss: fire-and-forget fetch, bump on completion so the
        // disabled state recalculates on the next render. Until the fetch
        // resolves, fall back to the generic cascade so the user isn't
        // wrongly blocked when they have plausible authority.
        _fetchLevels(entity_code, category).then(d => {
          if (d) bump();
        });
        const fallbackLimit = (generic[userLevel] ?? AGENT_ESCALATION[userLevel]?.limit ?? 50000) as number;
        return Math.abs(amount) <= fallbackLimit;
      }

      const limit = cascade[userLevel];
      if (limit === null || limit === undefined) return true; // unlimited
      return Math.abs(amount) <= (limit as number);
    },
    [userLevel, generic, bump],
  );

  return {
    canApprove,
    userLevel,
    userLimit: genericLimit,
    doaLevels: generic,
  };
}
